// Service de gestion des permissions côté frontend

class PermissionService {
  constructor() {
    this.permissions = null;
    this.planInfo = null;
    this.companyName = null;
    // Système de cache
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    this.pendingRequests = new Map(); // Pour éviter les requêtes simultanées
  }

  /**
   * Vérifier si le cache est valide pour une entreprise
   */
  isCacheValid(companyName) {
    const cacheEntry = this.cache.get(companyName);
    if (!cacheEntry) return false;
    
    const now = Date.now();
    return (now - cacheEntry.timestamp) < this.cacheTimeout;
  }

  /**
   * Obtenir les données depuis le cache
   */
  getCachedData(companyName) {
    const cacheEntry = this.cache.get(companyName);
    return cacheEntry ? cacheEntry.data : null;
  }

  /**
   * Mettre en cache les données
   */
  setCacheData(companyName, data) {
    this.cache.set(companyName, {
      data: data,
      timestamp: Date.now()
    });
  }

  /**
   * Initialiser les permissions pour une entreprise
   */
  async initializePermissions(companyName) {
    try {
      // Vérifier si une requête est déjà en cours pour cette entreprise
      if (this.pendingRequests.has(companyName)) {
        console.log('🔄 Requête déjà en cours pour', companyName);
        return await this.pendingRequests.get(companyName);
      }

      // Vérifier le cache d'abord
      if (this.isCacheValid(companyName)) {
        console.log('📦 Utilisation du cache pour', companyName);
        const cachedData = this.getCachedData(companyName);
        this.companyName = companyName;
        this.planInfo = cachedData;
        this.permissions = cachedData.features;
        return true;
      }

      console.log('🌐 Nouvelle requête API pour', companyName);
      
      // Créer une promesse pour la requête en cours
      const requestPromise = this._fetchPermissions(companyName);
      this.pendingRequests.set(companyName, requestPromise);

      try {
        const result = await requestPromise;
        return result;
      } finally {
        // Nettoyer la requête en cours
        this.pendingRequests.delete(companyName);
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'initialisation des permissions:', error);
      this.pendingRequests.delete(companyName);
      return false;
    }
  }

  /**
   * Effectuer la requête API
   */
  async _fetchPermissions(companyName) {
    this.companyName = companyName;
    
    // Utiliser une URL relative qui s'adaptera à l'environnement
    const baseUrl = window.location.origin;
    const response = await fetch(`${baseUrl}/subscriptions/company-plan`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        company_name: companyName
      }),
    });

    const data = await response.json();
    
    if (data.success) {
      this.planInfo = data.data;
      this.permissions = data.data.features;
      
      // Mettre en cache
      this.setCacheData(companyName, data.data);
      
      console.log('✅ Permissions initialisées et mises en cache:', this.permissions);
      return true;
    } else {
      console.error('❌ Erreur lors de la récupération des permissions:', data.error);
      return false;
    }
  }

  /**
   * Vérifier si une fonctionnalité est autorisée
   */
  hasFeature(featureName) {
    if (!this.permissions) {
      console.warn('⚠️ Permissions non initialisées');
      return false;
    }
    
    return this.permissions[featureName] === true;
  }

  /**
   * Obtenir les informations du plan
   */
  getPlanInfo() {
    return this.planInfo;
  }

  /**
   * Obtenir le plan actuel
   */
  getCurrentPlan() {
    return this.planInfo?.company?.subscription_plan || 'standard';
  }

  /**
   * Obtenir l'usage actuel
   */
  getCurrentUsage() {
    if (!this.planInfo || !this.planInfo.usage) {
      return {};
    }

    // Standardiser le format de l'usage
    const rawUsage = this.planInfo.usage;
    const formattedUsage = {};

    Object.entries(rawUsage).forEach(([key, value]) => {
      formattedUsage[key] = {
        current: typeof value === 'number' ? value : (value?.current || 0)
      };
    });

    return formattedUsage;
  }

  /**
   * Obtenir les limites du plan
   */
  getPlanLimits() {
    return this.planInfo?.plan?.limits || {};
  }

  /**
   * Vérifier si une limite est dépassée
   */
  isLimitExceeded(limitType) {
    const usage = this.getCurrentUsage();
    const limits = this.getPlanLimits();
    
    if (!usage[limitType] || !limits[limitType]) {
      return false;
    }
    
    const limit = limits[limitType];
    if (limit === -1) return false; // Illimité
    
    return usage[limitType].current >= limit;
  }

  /**
   * Obtenir le pourcentage d'usage
   */
  getUsagePercentage(limitType) {
    const usage = this.getCurrentUsage();
    const limits = this.getPlanLimits();
    
    if (!usage[limitType] || !limits[limitType]) {
      return 0;
    }
    
    const limit = limits[limitType];
    if (limit === -1) return 0; // Illimité
    
    return Math.round((usage[limitType].current / limit) * 100);
  }

  /**
   * Forcer le rafraîchissement du cache
   */
  async refreshCache(companyName) {
    console.log('🔄 Rafraîchissement forcé du cache pour', companyName);
    // Supprimer du cache
    this.cache.delete(companyName);
    // Réinitialiser
    return await this.initializePermissions(companyName);
  }

  /**
   * Nettoyer le cache expiré
   */
  cleanExpiredCache() {
    const now = Date.now();
    for (const [companyName, cacheEntry] of this.cache.entries()) {
      if ((now - cacheEntry.timestamp) >= this.cacheTimeout) {
        console.log('🗑️ Suppression du cache expiré pour', companyName);
        this.cache.delete(companyName);
      }
    }
  }

  /**
   * Fonctionnalités disponibles
   */
  static FEATURES = {
    PDF_GENERATION: 'pdf_generation',
    FORM_CREATION: 'form_creation',
    API_ACCESS: 'api_access',
    ADVANCED_STATS: 'advanced_stats',
    BULK_OPERATIONS: 'bulk_operations',
    AI_INSIGHTS: 'ai_insights',
    ADVANCED_AUTOMATION: 'advanced_automation',
    CUSTOM_INTEGRATIONS: 'custom_integrations',
    PRIORITY_SUPPORT: 'priority_support',
    WHITE_LABEL: 'white_label'
  };

  /**
   * Plans disponibles
   */
  static PLANS = {
    STANDARD: 'standard',
    BUSINESS: 'business', 
    PREMIUM: 'premium'
  };
}

// Créer une instance singleton
const permissionService = new PermissionService();

// Nettoyer le cache périodiquement
setInterval(() => {
  permissionService.cleanExpiredCache();
}, 60 * 1000); // Toutes les minutes

export default permissionService; 